import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";

/** Iter 659a — public "Recent winners" strip at the top of /events.
 *  GET /classifieds/prize-draws/recent-winners (no auth). Names arrive
 *  privacy-formatted server-side ("Val T.") — rendered as-is. Empty or
 *  error → renders nothing. */

const EMOJI = { points: "💰", voucher: "🎟️", gift: "🎁" };

function ago(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

export default function RecentWinnersStrip() {
  const q = useQuery({
    queryKey: ["prize-recent-winners"],
    queryFn: async () =>
      (await sharedApi.get("/classifieds/prize-draws/recent-winners", { params: { limit: 20 } })).data,
    staleTime: 60_000,
    retry: 0,
  });

  const winners = q.data?.winners || [];
  if (q.isLoading || q.isError || winners.length === 0) return null;

  return (
    <div
      data-testid="cev-winner-wall"
      className="mt-8 rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="font-display font-black text-slate-900">🏆 Recent winners</div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-700/70">
          Real people, real draws
        </div>
      </div>
      <div className="mt-3 flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
        {winners.map((w, i) => (
          <Link
            key={`${w.draw_id}-${i}`}
            to={`/events/${w.event_id}`}
            data-testid={`cev-winner-${i}`}
            className="shrink-0 w-[200px] rounded-xl bg-white border border-amber-100 shadow-sm p-3 hover:border-amber-300 transition-colors"
          >
            <div className="text-lg leading-none" aria-hidden>{EMOJI[w.prize_type] || "🏆"}</div>
            <div className="mt-1.5 text-sm font-bold text-slate-900 truncate">{w.name}</div>
            <div className="text-xs font-bold text-amber-700 truncate">won {w.prize}</div>
            <div className="mt-1 text-[11px] text-slate-400 truncate">
              {w.event_title} · {ago(w.drawn_at)}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
